import { ROLE_CONFIG } from "./constants";

/**
 * Utilidades de formateo
 */

/**
 * Obtiene la configuración de un rol
 * @param {string} role - Rol del usuario
 * @returns {Object} Configuración del rol
 */
export const getRoleConfig = (role) => {
  const roleKey = (role || "").toLowerCase();
  return ROLE_CONFIG[roleKey] || ROLE_CONFIG.default;
};

/**
 * Formatea un número con separadores de miles
 * @param {number} num - Número a formatear
 * @returns {string} Número formateado
 */
export const formatNumber = (num) => {
  if (num === null || num === undefined) return "N/D";
  return new Intl.NumberFormat("es-MX").format(num);
};

/**
 * Formatea una fecha relativa (ej: "hace 2 horas")
 * @param {string|Date} date - Fecha a formatear
 * @returns {string} Fecha formateada
 */
export const formatRelativeTime = (date) => {
  const now = new Date();
  const then = new Date(date);
  const diffInSeconds = Math.floor((now - then) / 1000);

  if (diffInSeconds < 60) return "hace un momento";
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `hace ${minutes} minuto${minutes > 1 ? "s" : ""}`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `hace ${hours} hora${hours > 1 ? "s" : ""}`;
  }
  const days = Math.floor(diffInSeconds / 86400);
  return `hace ${days} día${days > 1 ? "s" : ""}`;
};

/**
 * Trunca un texto largo
 * @param {string} text - Texto a truncar
 * @param {number} maxLength - Longitud máxima
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength = 50) => {
  if (!text || text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

/**
 * Capitaliza la primera letra de una cadena
 * @param {string} str - Cadena a capitalizar
 * @returns {string} Cadena capitalizada
 */
export const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/**
 * Obtiene las iniciales de un nombre
 * @param {string} name - Nombre completo
 * @returns {string} Iniciales
 */
export const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Convierte una hora en formato HH:mm a minutos desde medianoche
 * @param {string} timeString - Hora en formato HH:mm
 * @returns {number} Minutos desde medianoche
 */
export const timeToMinutes = (timeString) => {
  if (!timeString) return null;
  const [hours, minutes] = timeString.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Valida si una hora de servicio es válida respecto a la hora de cierre de la sucursal
 * El servicio debe programarse DESPUÉS de la hora de cierre (cuando la sucursal está cerrada)
 * También permite servicios en la madrugada (00:00 - 06:00) del día siguiente
 * @param {string} serviceDate - Fecha del servicio en formato YYYY-MM-DD
 * @param {string} serviceHour - Hora del servicio en formato HH:mm
 * @param {string} closingHour - Hora de cierre de la sucursal en formato HH:mm
 * @returns {Object} { isValid: boolean, errorMessage: string }
 */
export const validateClosingHour = (serviceDate, serviceHour, closingHour) => {
  // Si no hay hora de cierre, la validación pasa
  if (!closingHour) {
    return { isValid: true, errorMessage: null };
  }

  // Si no hay fecha u hora de servicio, no validar aún
  if (!serviceDate || !serviceHour) {
    return { isValid: true, errorMessage: null };
  }

  // Convertir a minutos desde medianoche
  const serviceMinutes = timeToMinutes(serviceHour);
  const closingMinutes = timeToMinutes(closingHour);
  const SIX_AM_MINUTES = 6 * 60; // 360 minutos (6:00 AM)

  if (serviceMinutes === null || closingMinutes === null) {
    return { isValid: true, errorMessage: null };
  }

  // Permitir servicios en la madrugada (00:00 - 06:00) - se asume que es del día siguiente
  if (serviceMinutes <= SIX_AM_MINUTES) {
    return { isValid: true, errorMessage: null };
  }

  // El servicio DEBE ser A PARTIR de la hora de cierre (cuando está cerrada o cerrando)
  // Si serviceMinutes es menor a closingMinutes, significa que es ANTES del cierre
  if (serviceMinutes < closingMinutes) {
    return {
      isValid: false,
      errorMessage: `El servicio debe programarse a partir de la hora de cierre (${closingHour}) o en la madrugada (00:00 - 06:00)`,
    };
  }

  // Si llegamos aquí, el servicio es a la hora de cierre o después, es válido
  return { isValid: true, errorMessage: null };
};

